/* import __COLOCATED_TEMPLATE__ from './bar-with-percentage.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type ChartDataResource from 'embercom/lib/reporting/chart-data-resource';
import type {
  PercentageMetricValue,
  MetricValue,
} from 'embercom/lib/reporting/chart-data-resource';
import { tracked } from '@glimmer/tracking';

interface SegmentObject {
  label: string;
  barColorClass: string;
  key?: string;
  gapAfter?: boolean;
  tooltipText?: string;
  headerTitle?: string;
  rawValue?: number;
}

export interface Segment extends SegmentObject {
  share: number;
  showInLegend: boolean;
}

interface Signature {
  Args: {
    dataResource: ChartDataResource;
    objects: Record<string, SegmentObject>;
    metricId: string;
    showTopLineMetric: boolean;
    isHeadlineMetric: boolean;
    headlineDataResource?: ChartDataResource;
    hasSeparateCounterMetric?: boolean;
    openUnderlyingDataModal: (segment: Segment) => void;
  };
}

export default class BarWithPercentage extends Component<Signature> {
  @tracked hoveredSegment: Segment | undefined;
  get totalPercentage() {
    if (this.args.hasSeparateCounterMetric) {
      let value = this.args.headlineDataResource?.kpiData[0] as PercentageMetricValue;
      return this.args.headlineDataResource?.formatValue({
        value: {
          value: Math.round(value?.value || 0),
          denominatorValue: value?.denominatorValue,
          numeratorValue: value?.numeratorValue,
        },
        metricId: this.args.metricId,
      });
    }
    let value = this.args.dataResource.kpiData[0] as PercentageMetricValue;
    return this.args.dataResource.formatValue({
      value: {
        value: Math.round(value?.value || 0),
        denominatorValue: value?.denominatorValue,
        numeratorValue: value?.numeratorValue,
      },
      metricId: this.args.metricId,
    });
  }

  get hasViewBy() {
    return this.args.dataResource.renderableChart.viewBy !== undefined;
  }

  get values() {
    if (this.hasViewBy) {
      return this.args.dataResource.flatCombinedData;
    } else {
      let value = this.args.dataResource.kpiData[0] as PercentageMetricValue;
      return {
        numerator: value?.numeratorValue,
        denominator: value?.denominatorValue,
      };
    }
  }

  get totalValue() {
    if (this.hasViewBy) {
      let total = 0;
      let flatCombinedData = this.args.dataResource.flatCombinedData;
      Object.values(flatCombinedData).forEach((value: unknown) => {
        if (Array.isArray(value) && typeof value[0] === 'number') {
          total += value[0];
        }
      });
      return total;
    } else {
      let value = this.args.dataResource.kpiData[0] as PercentageMetricValue;
      return value?.denominatorValue ?? 0;
    }
  }

  @action
  formattedValue(value: number | undefined) {
    if (!value) {
      return '0';
    }
    let metricValue = {
      value,
    } as MetricValue;
    return this.args.dataResource.formatValue({
      value: metricValue,
      metricId: 'v1.new_conversations',
    });
  }

  get needsTwoColumns() {
    return (
      Object.keys(this.args.dataResource.flatCombinedData).length > 1 && !this.args.isHeadlineMetric
    );
  }

  @action
  needsGap(segment: Segment) {
    return (
      segment.gapAfter &&
      (!this.hasViewBy || Object.keys(this.args.dataResource.flatCombinedData).length > 1)
    );
  }

  get segments(): Segment[] {
    if (!this.args.objects) {
      return [];
    }
    let flatCombinedData = this.args.dataResource.flatCombinedData;
    if (this.hasViewBy) {
      return Object.keys(this.args.objects).map((key) => {
        let dataKey = key.includes('key_') ? key.split('key_')[1] : key;
        let obj = this.args.objects[key];
        let value = (flatCombinedData[dataKey]?.[0] as number) ?? 0;
        let share = this.totalValue > 0 ? (value / this.totalValue) * 100 : 0;
        return {
          ...obj,
          share: obj.gapAfter ? share - 1 : share,
          rawValue: value,
          key,
          showInLegend: value > 0,
        };
      });
    } else {
      // Rates format with array: numerator/denominator from kpiData
      let kpiData = this.args.dataResource.kpiData[0] as PercentageMetricValue;
      let objects = this.args.objects;
      return Object.keys(objects).map((key) => {
        let obj = objects[key];
        let share = 0;
        let rawValue = 0;

        if (key === 'numerator' || key.includes('numerator')) {
          share =
            this.totalValue > 0 ? Math.round((kpiData.numeratorValue / this.totalValue) * 100) : 0;
          rawValue = kpiData.numeratorValue;
        } else {
          let value = kpiData.denominatorValue - kpiData.numeratorValue;
          share = this.totalValue > 0 ? Math.round((value / this.totalValue) * 100) : 0;
          rawValue = kpiData.denominatorValue;
        }
        return {
          ...obj,
          share: obj.gapAfter ? share - 1 : share,
          rawValue,
          key,
          showInLegend: true,
        };
      });
    }
  }
  get firstColumnSegments() {
    return this.segments.slice(0, 3);
  }

  get secondColumnSegments() {
    return this.segments.slice(3);
  }

  get gridTemplateColumns() {
    let spans = this.segments.map((segment) => (segment.share > 0 ? segment.share : 1));
    return spans.map((span) => `${span}fr`).join(' ');
  }

  get shouldFillBar() {
    return this.hoveredSegment && this.hoveredSegment.key === 'denominator';
  }

  @action
  shouldUseOpacityOnSegment(segment: Segment) {
    if (this.shouldFillBar) {
      return false;
    }

    return this.hoveredSegment !== segment && this.hoveredSegment !== undefined;
  }

  @action
  openUnderlyingDataModal(segment: Segment) {
    this.args.openUnderlyingDataModal(segment);
  }

  @action
  onSegmentHovered(segment: Segment) {
    this.hoveredSegment = segment;
  }

  @action
  onSegmentExited() {
    this.hoveredSegment = undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::BarWithPercentage': typeof BarWithPercentage;
    'reporting/bespoke/fin-performance/bar-with-percentage': typeof BarWithPercentage;
  }
}
